"use client";

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function VideoDatesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const benefits = [
  {
    id: 'benefit_comfort',
    title: 'Date from Home',
    description: 'No pressure to dress up or travel. Meet from your couch.'
  },
  {
    id: 'benefit_safety',
    title: 'Safe First Interaction',
    description: 'Get to know them before sharing personal contact info.'
  },
  {
    id: 'benefit_chemistry',
    title: 'Test Chemistry',
    description: 'See if there\'s a spark before committing to an in-person date.'
  },
  {
    id: 'benefit_efficient',
    title: 'Save Time',
    description: 'Skip incompatible matches faster without wasting an evening.'
  }];


  return (
    <section ref={sectionRef} className="py-24 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Video Preview */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative">
              <div className="glass-card p-4 rounded-3xl">
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
                  <AppImage
                    src="https://images.unsplash.com/photo-1586985564259-6211deb4c122"
                    alt="Two people on video call smiling and having engaging conversation through MeetX video dating feature"
                    className="w-full h-full object-cover" />
                  
                  {/* Video Call UI Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                      <span className="text-white text-sm font-medium">Video Date Active</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Icon name="MicrophoneIcon" size={20} className="text-white" />
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Icon name="VideoCameraIcon" size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative Glow */}
              <div className="absolute -top-12 -left-12 w-64 h-64 bg-secondary/20 rounded-full blur-[100px] pointer-events-none" />
            </div>
          </div>

          {/* Right: Content */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 text-secondary text-xs uppercase tracking-widest font-semibold mb-6">
              <Icon name="VideoCameraIcon" variant="solid" size={16} />
              Unique Feature
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
              Video Dating <span className="text-primary">Built In</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Chat face-to-face before committing to an in-person meetup. Our built-in video dating feature is safe, convenient, and helps you gauge real chemistry from the comfort of home.
            </p>

            {/* Benefits Grid */}
            <div className="grid grid-cols-2 gap-4">
              {benefits?.map((benefit, index) =>
              <div
                key={benefit?.id}
                className="glass-card p-4 rounded-xl transition-all duration-500"
                style={{ transitionDelay: `${index * 100 + 400}ms` }}>
                
                  <h4 className="font-semibold text-foreground mb-2 text-sm">{benefit?.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{benefit?.description}</p>
                </div>
              )}
            </div>

            <div className="mt-8 p-6 glass-card rounded-xl border-l-4 border-secondary">
              <p className="text-sm text-muted-foreground italic">
                &quot;The video date feature is a game-changer. I saved so much time by filtering out incompatible matches before wasting an evening.&quot;
              </p>
              <p className="text-xs text-foreground font-semibold mt-2">- Jessica M., San Francisco</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}